import {
  CheckCircleOutlined,
  DisconnectOutlined,
  LoadingOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  message,
  Modal,
  Progress,
  Spin,
  Typography,
} from "antd";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import PageHeader from "../../Routes/PageHeader";

const { Text } = Typography;

export default function WhatsAppIntegration() {
  const [qrCodeValue, setQrCodeValue] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("Disconnected");
  const [qrTimer, setQrTimer] = useState(20);

  // const API_URL = "http://localhost:8006";
  const API_URL = "https://sahari-messageing-server.bairuhatech.com";

  const checkConnectionStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/status`);
      if (!response.ok) throw new Error("Status check failed");
      const data = await response.json();

      const newStatus = data.connected
        ? "Connected"
        : data.authenticated
        ? "Authenticated"
        : "Disconnected";

      // If status changes to Connected or Authenticated, close modal and show success message
      if (
        (newStatus === "Connected" || newStatus === "Authenticated") &&
        connectionStatus !== newStatus &&
        isModalVisible
      ) {
        setIsModalVisible(false);
        message.success(`WhatsApp ${newStatus.toLowerCase()} successfully.`);
      }

      setConnectionStatus(newStatus);
    } catch (error) {
      console.error("Error checking status:", error);
      setConnectionStatus("Error");
    }
  }, [API_URL, connectionStatus, isModalVisible]);

  useEffect(() => {
    checkConnectionStatus();
    const interval = setInterval(checkConnectionStatus, 5000);
    return () => clearInterval(interval);
  }, [checkConnectionStatus]);

  // Effect to automatically close the modal when connection is established
  useEffect(() => {
    if (
      (connectionStatus === "Authenticated" ||
        connectionStatus === "Connected") &&
      isModalVisible
    ) {
      setIsModalVisible(false);
      message.success(
        `WhatsApp ${connectionStatus.toLowerCase()} successfully.`
      );
    }
  }, [connectionStatus, isModalVisible]);

  const fetchQRCode = useCallback(
    debounce(async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/qr`);
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        if (data.qr) {
          setQrCodeValue(data.qr);
          setQrTimer(20);
          setIsModalVisible(true);
        } else {
          message.info("No QR code available. Try generating a new QR code.");
          checkConnectionStatus();
        }
      } catch (error) {
        console.error("Error fetching QR code:", error);
        message.error("Failed to fetch QR code. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [API_URL, checkConnectionStatus]
  );

  const generateNewQR = useCallback(
    debounce(async () => {
      try {
        setIsLoading(true);
        setQrCodeValue("");
        const response = await fetch(`${API_URL}/reconnect`, {
          method: "POST",
        });
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        if (data.success) {
          message.success("QR code generation initiated");
          setConnectionStatus("Disconnected");
          setTimeout(fetchQRCode, 2000);
        } else {
          message.error(data.message || "Failed to generate new QR code");
        }
      } catch (error) {
        console.error("Error generating new QR code:", error);
        message.error("Failed to generate new QR code");
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [API_URL, fetchQRCode]
  );

  const disconnect = useCallback(
    debounce(async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/disconnect`, {
          method: "POST",
        });
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        if (data.success) {
          message.success(
            "Disconnected successfully. A new QR code has been generated."
          );
          setConnectionStatus("Disconnected");
          setQrCodeValue("");
          setTimeout(fetchQRCode, 2000);
        } else {
          message.error(data.message || "Failed to disconnect");
        }
      } catch (error) {
        console.error("Error disconnecting:", error);
        message.error("Failed to disconnect");
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [API_URL, fetchQRCode]
  );

  useEffect(() => {
    if (isModalVisible && qrCodeValue && !isLoading) {
      const timerInterval = setInterval(() => {
        setQrTimer((prev) => {
          if (prev <= 1) {
            fetchQRCode();
            return 20;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerInterval);
    }
  }, [isModalVisible, qrCodeValue, fetchQRCode, isLoading]);

  const getStatusConfig = (status) => {
    switch (status) {
      case "Connected":
        return {
          color: "green",
          icon: <CheckCircleOutlined />,
          message: "WhatsApp is fully connected.",
        };
      case "Authenticated":
        return {
          color: "blue",
          icon: <CheckCircleOutlined />,
          message: "WhatsApp is authenticated.",
        };
      case "Error":
        return {
          color: "red",
          icon: <WarningOutlined />,
          message: "Error connecting to WhatsApp.",
        };
      default:
        return {
          color: "gray",
          icon: <WarningOutlined />,
          message: "WhatsApp is not connected. Scan a QR code to connect.",
        };
    }
  };

  const {
    color,
    icon,
    message: statusMessage,
  } = getStatusConfig(connectionStatus);

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
      <PageHeader
        PageName={"Whatsapp QR"}
        breadScrum={"Admin / Whatsapp QR Scanner"}
      />
      <Card title="WhatsApp Integration">
        <div className="space-y-4">
          <Alert
            message={
              <div className="flex items-center gap-2">
                {icon}
                <span>
                  Status: <Badge color={color} text={connectionStatus} />
                </span>
              </div>
            }
            description={statusMessage}
            type={
              connectionStatus === "Connected"
                ? "success"
                : connectionStatus === "Authenticated"
                ? "info"
                : connectionStatus === "Error"
                ? "error"
                : "warning"
            }
            showIcon
            className="rounded-lg"
          />
          <br />
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              type="primary"
              size="large"
              onClick={fetchQRCode}
              disabled={isLoading || connectionStatus === "Connected"}
              loading={isLoading}
              className="w-40"
            >
              Show QR Code
            </Button>
            <Button
              type="default"
              size="large"
              icon={<ReloadOutlined />}
              onClick={generateNewQR}
              disabled={isLoading || connectionStatus === "Connected"}
              className="w-40"
            >
              Generate New QR
            </Button>
            {connectionStatus === "Connected" && (
              <Button
                type="default"
                size="large"
                icon={<DisconnectOutlined />}
                onClick={disconnect}
                disabled={isLoading}
                danger
                className="w-40"
              >
                Disconnect
              </Button>
            )}
          </div>
          <br />
          <div className="bg-gray-50 p-4 rounded-lg text-center border border-gray-200">
            <Text className="font-semibold text-gray-700">
              Connection Status:{" "}
            </Text>
            <Text className={`font-bold text-${color}-600`}>
              {connectionStatus}
            </Text>
          </div>
        </div>

        <Modal
          title="Scan WhatsApp QR Code"
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button
              key="refresh"
              type="primary"
              onClick={fetchQRCode}
              disabled={isLoading}
            >
              Refresh QR
            </Button>,
            <Button
              key="new"
              type="default"
              onClick={generateNewQR}
              disabled={isLoading}
            >
              Generate New QR
            </Button>,
            <Button key="close" onClick={() => setIsModalVisible(false)}>
              Close
            </Button>,
          ]}
          centered
        >
          <div className="flex flex-col items-center p-4 space-y-4">
            {isLoading ? (
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              />
            ) : qrCodeValue ? (
              <>
                <div className="p-4 bg-white border rounded-lg shadow-sm d-flex justify-content-center">
                  <QRCode value={qrCodeValue} size={200} />
                </div>
                <Progress
                  percent={(qrTimer / 20) * 100}
                  status="active"
                  format={() => `${qrTimer}s`}
                  className="w-32"
                />
                <Alert
                  message="Scan with WhatsApp"
                  description={
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                      <li>Open WhatsApp on your phone.</li>
                      <li>Go to Settings - Linked Devices.</li>
                      <li>Tap "Link a Device" and scan this QR code.</li>
                      <li>
                        Ensure your phone has an active internet connection.
                      </li>
                      <li>
                        If linking fails, try disconnecting or generating a new
                        QR code.
                      </li>
                    </ul>
                  }
                  type="info"
                  showIcon
                  className="w-full"
                />
              </>
            ) : (
              <Alert
                message="No QR Code Available"
                description="Click 'Generate New QR' to create a new QR code."
                type="warning"
                showIcon
                className="w-full"
              />
            )}
          </div>
        </Modal>
      </Card>
    </div>
  );
}
